'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { clientService } from '@/lib/services/clientService';

interface AccessFormProps {
  onAccessGranted: (galleryData: GalleryData) => void;
}

export interface GalleryData {
  id: string;
  name: string;
  description: string | null;
  photoCount: number;
  photos: Array<{
    id: string;
    image: string;
    alt: string;
    title: string;
  }>;
}

export default function AccessForm({ onAccessGranted }: AccessFormProps) {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate access code
      const payload = await clientService.fetchClientGalleryByCode(accessCode);

      if (!payload) {
        setError('Invalid access code. Please check your email for the correct code.');
        return;
      }

      const { gallery, photos } = payload;

      // Transform to expected format
      const galleryData: GalleryData = {
        id: gallery.id,
        name: gallery.name,
        description: gallery.description,
        photoCount: photos.length,
        photos: photos.map((photo) => ({
          id: photo.id,
          image: photo.url,
          alt: photo.description ?? photo.title ?? 'Photo',
          title: photo.title ?? '',
        })),
      };

      onAccessGranted(galleryData);
    } catch (err: any) {
      console.error('Access code validation error:', err);
      setError(err.message || 'An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="glass-panel rounded-2xl p-8 space-y-6">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center">
          <Icon name="LockClosedIcon" size={32} className="text-white" />
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-white">Enter Access Code</h2>
          <p className="text-sm text-muted-foreground">
            Enter the unique code provided via email to access your private gallery
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="XXXXXXXXXX"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
              required
            />
          </div>

          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !accessCode.trim()}
            className="w-full px-6 py-3 bg-white text-background rounded-lg font-medium hover:bg-white/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                Access My Gallery
                <Icon name="ArrowRightIcon" size={20} />
              </>
            )}
          </button>
        </form>

        {/* Help Text */}
        <div className="text-center pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Lost your access code?{' '}
            <a href="mailto:ryansukeena@gmail.com" className="text-white hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
